import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { api, type CrmOrder, type CrmOrderStatusUpdatePayload } from "@/lib/api";

type OrderStatus = CrmOrderStatusUpdatePayload["status"];

const STATUS_LABELS: Record<string, string> = {
  payment_pending: "Ожидает оплаты",
  paid: "Оплачен",
  processing: "В работе",
  completed: "Завершен",
  cancelled: "Отменен",
  refunded: "Возврат",
};

const STATUS_STYLES: Record<string, string> = {
  payment_pending: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  processing: "bg-orange-50 text-orange-700 border-orange-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  refunded: "bg-rose-50 text-rose-700 border-rose-200",
};

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: "payment_pending", label: STATUS_LABELS.payment_pending },
  { value: "paid", label: STATUS_LABELS.paid },
  { value: "processing", label: STATUS_LABELS.processing },
  { value: "completed", label: STATUS_LABELS.completed },
  { value: "cancelled", label: STATUS_LABELS.cancelled },
  { value: "refunded", label: STATUS_LABELS.refunded },
];

const SOURCE_LABELS: Record<string, string> = {
  web: "Сайт",
  telegram: "Telegram",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(value: string | number) {
  return `${Number(value || 0).toLocaleString("ru-RU")} ₽`;
}

function OrderStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${
        STATUS_STYLES[status] || "border-gray-200 bg-gray-50 text-gray-600"
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState<CrmOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const loadOrder = useCallback(async () => {
    if (!orderId) {
      setError("Не указан идентификатор заказа");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const nextOrder = await api.order(orderId);
      setOrder(nextOrder);
      setCommentDraft(nextOrder.manager_comment ?? "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось загрузить заказ");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void loadOrder();
  }, [loadOrder]);

  const handleStatusChange = useCallback(
    async (nextStatus: OrderStatus) => {
      if (!order || statusSaving) return;

      setStatusSaving(true);
      setActionError(null);
      try {
        const updatedOrder = await api.updateOrderStatus(order.id, { status: nextStatus });
        setOrder(updatedOrder);
        setCommentDraft(updatedOrder.manager_comment ?? "");
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Не удалось обновить статус заказа");
      } finally {
        setStatusSaving(false);
      }
    },
    [order, statusSaving],
  );

  const handleCommentSave = useCallback(async () => {
    if (!order || commentSaving) return;

    setCommentSaving(true);
    setActionError(null);
    try {
      const updatedOrder = await api.updateOrderManagerComment(order.id, {
        manager_comment: commentDraft.trim() || null,
      });
      setOrder(updatedOrder);
      setCommentDraft(updatedOrder.manager_comment ?? "");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Не удалось сохранить комментарий менеджера");
    } finally {
      setCommentSaving(false);
    }
  }, [commentDraft, commentSaving, order]);

  const handleCopyLink = useCallback(async () => {
    setCopyMessage(null);
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopyMessage("Ссылка скопирована");
    } catch {
      setCopyMessage(url);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-80 flex-col items-center justify-center gap-3 rounded-xl border border-[#E8E2D8] bg-white">
        <i className="ri-loader-4-line animate-spin text-xl text-amber-600" />
        <span className="text-sm text-gray-500">Загрузка заказа...</span>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
        <p>{error || "Заказ не найден"}</p>
        <Link to="/orders" className="mt-3 inline-flex items-center gap-1.5 font-medium text-red-800 hover:text-red-900">
          <i className="ri-arrow-left-line" />
          Вернуться к заказам
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link to="/orders" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900">
            <i className="ri-arrow-left-line" />
            Все заказы
          </Link>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">{order.order_number}</h2>
            <OrderStatusBadge status={order.status} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleCopyLink}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-amber-400 hover:text-gray-900"
          >
            <i className="ri-link" />
            Скопировать ссылку
          </button>
          <button
            type="button"
            onClick={() => void loadOrder()}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-amber-400 hover:text-gray-900"
          >
            <i className="ri-refresh-line" />
            Обновить
          </button>
        </div>
      </div>

      {copyMessage ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{copyMessage}</div> : null}
      {actionError ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{actionError}</div> : null}

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Источник</p>
          <p className="mt-3 text-sm font-medium text-gray-800">{SOURCE_LABELS[order.source] || order.source}</p>
        </div>
        <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Сумма заказа</p>
          <p className="mt-3 text-sm font-semibold text-gray-900">{formatMoney(order.total_amount)}</p>
        </div>
        <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Создан</p>
          <p className="mt-3 text-sm font-medium text-gray-800">{formatDate(order.created_at)}</p>
        </div>
      </div>

      <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Статус</p>
            <p className="mt-2 text-sm text-gray-600">Изменение сохраняется сразу в заказе.</p>
          </div>
          <select
            value={order.status}
            onChange={(event) => void handleStatusChange(event.target.value as OrderStatus)}
            disabled={statusSaving}
            className="w-full rounded-lg border border-[#E8E2D8] bg-[#FAFAF8] px-3 py-2 text-sm text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 disabled:cursor-not-allowed disabled:opacity-60 sm:max-w-60"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Получатель</p>
          <p className="mt-3 text-sm font-medium text-gray-900">{order.recipient.full_name}</p>
          <p className="mt-1 text-sm text-gray-600">{order.recipient.phone}</p>
          <p className="mt-1 text-sm text-gray-600">{order.recipient.email || "Email не указан"}</p>
        </div>
        <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Даты</p>
          <p className="mt-3 text-sm text-gray-700">
            <span className="font-medium text-gray-900">Создан:</span> {formatDate(order.created_at)}
          </p>
          <p className="mt-1 text-sm text-gray-700">
            <span className="font-medium text-gray-900">Оплачен:</span> {order.paid_at ? formatDate(order.paid_at) : "еще не оплачен"}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Адрес доставки</p>
        <p className="mt-3 text-sm leading-6 text-gray-700">{order.recipient.delivery_address}</p>
      </div>

      <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Комментарий менеджера</p>
            <p className="mt-2 text-sm text-gray-600">Внутренняя заметка видна только в CRM.</p>
          </div>
          <button
            type="button"
            onClick={() => void handleCommentSave()}
            disabled={commentSaving || commentDraft.trim() === (order.manager_comment ?? "")}
            className="inline-flex w-fit items-center justify-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {commentSaving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-3-line" />}
            {commentSaving ? "Сохраняем..." : "Сохранить"}
          </button>
        </div>
        <textarea
          value={commentDraft}
          onChange={(event) => setCommentDraft(event.target.value)}
          maxLength={2000}
          rows={4}
          className="mt-4 w-full resize-y rounded-lg border border-[#E8E2D8] bg-[#FAFAF8] px-3 py-2 text-sm leading-6 text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
        />
      </div>

      <div className="rounded-xl border border-[#E8E2D8] bg-white">
        <div className="border-b border-[#E8E2D8] px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">Состав заказа</p>
        </div>
        <div className="divide-y divide-[#F0EDE6]">
          {order.items.map((item) => (
            <div key={item.id} className="grid gap-3 px-4 py-4 md:grid-cols-[minmax(260px,1fr)_120px_70px_110px]">
              <div>
                <p className="text-sm font-medium text-gray-900">{item.title}</p>
                <p className="mt-1 text-xs text-gray-500">{item.sku_label || "SKU не указан"}</p>
                {item.components?.length ? (
                  <div className="mt-3 rounded-lg border border-[#F0EDE6] bg-[#FAF8F5] px-3 py-2">
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Состав</p>
                    <div className="space-y-1.5">
                      {item.components.map((component) => (
                        <div key={component.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 text-xs text-gray-600">
                          <div className="min-w-0">
                            <p className="break-words font-medium text-gray-700">{component.title}</p>
                            {component.sku_label || component.comment ? (
                              <p className="mt-0.5 break-words text-gray-500">
                                {[component.sku_label, component.comment].filter(Boolean).join(" · ")}
                              </p>
                            ) : null}
                          </div>
                          <span className="shrink-0 text-gray-500">× {component.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Цена</p>
                <p className="mt-1 text-sm text-gray-700">{formatMoney(item.price)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Кол-во</p>
                <p className="mt-1 text-sm text-gray-700">{item.quantity}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Сумма</p>
                <p className="mt-1 text-sm font-semibold text-gray-900">{formatMoney(item.subtotal)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
